import { COL_PRODUCT_MACHINES } from '../config/collections'
import { pb, PB_NO_CANCEL } from '../pocketbase'
import type { MachineRecord, ProductMachineRecord } from '../types/records'

type ProductMachinePayload = {
  product: string
  machine: string
  sort: number
  isPrimary: boolean
}

/** Явная отправка bool через FormData — иначе false может теряться при required bool в PB. */
function upsertProductMachine(id: string | undefined, payload: ProductMachinePayload) {
  const formData = new FormData()
  formData.append('@jsonPayload', JSON.stringify(payload))
  if (id) {
    return pb.collection(COL_PRODUCT_MACHINES).update(id, formData)
  }
  return pb.collection(COL_PRODUCT_MACHINES).create(formData)
}

export async function loadProductMachines(productId: string): Promise<ProductMachineRecord[]> {
  if (!productId) return []
  return pb.collection(COL_PRODUCT_MACHINES).getFullList<ProductMachineRecord>({
    filter: `product="${productId}"`,
    expand: 'machine',
    sort: '+sort',
    ...PB_NO_CANCEL,
  })
}

export async function loadCompatibleMachines(productId: string): Promise<MachineRecord[]> {
  const links = await loadProductMachines(productId)
  return links
    .map((l) => l.expand?.machine)
    .filter((m): m is MachineRecord => Boolean(m))
}

export async function saveProductMachines(
  productId: string,
  machineIds: string[],
  primaryId?: string | null,
): Promise<void> {
  const existing = await loadProductMachines(productId)
  const targetIds = new Set(machineIds)

  for (const row of existing) {
    if (!targetIds.has(row.machine)) {
      await pb.collection(COL_PRODUCT_MACHINES).delete(row.id)
    }
  }

  const resolvedPrimaryId =
    primaryId && machineIds.includes(primaryId)
      ? primaryId
      : (existing.find((e) => e.isPrimary && targetIds.has(e.machine))?.machine ?? machineIds[0] ?? '')

  let sort = 0
  for (const machineId of machineIds) {
    sort += 10
    const prev = existing.find((e) => e.machine === machineId)
    const isPrimary = machineId === resolvedPrimaryId
    const payload: ProductMachinePayload = {
      product: productId,
      machine: machineId,
      sort,
      isPrimary,
    }
    await upsertProductMachine(prev?.id, payload)
  }
}

export function primaryMachine(links: ProductMachineRecord[]): MachineRecord | null {
  const primary = links.find((l) => l.isPrimary)?.expand?.machine
  if (primary) return primary
  return links.find((l) => l.expand?.machine)?.expand?.machine ?? null
}

export async function loadProductMachineIds(productId: string): Promise<string[]> {
  const links = await loadProductMachines(productId)
  return links.map((l) => l.machine)
}

export async function loadMachineIdsByProductIds(
  productIds: string[],
): Promise<Map<string, string[]>> {
  const map = new Map<string, string[]>()
  const unique = [...new Set(productIds.filter(Boolean))]
  await Promise.all(
    unique.map(async (productId) => {
      map.set(productId, await loadProductMachineIds(productId))
    }),
  )
  return map
}

export function productHasCompatibleMachines(
  productId: string,
  machineIdsByProduct: Map<string, string[]> | Record<string, string[]>,
): boolean {
  const ids =
    machineIdsByProduct instanceof Map
      ? machineIdsByProduct.get(productId)
      : machineIdsByProduct[productId]
  return Boolean(ids && ids.length > 0)
}
